<template>
  <div>
    <BlBackdrop
      title="Create new availability"
      :label="submitLoading ? 'Creating' : 'Create'"
      :submitLoading="submitLoading"
      :disabled="isDisabledSubmit"
      @backdrop-close="$emit('close-modal')"
      @backdrop-action="onSubmit"
    >
      <div
        :class="{
          'overflow-y-auto': conflictedByRoom.length || conflictedByUser.length
        }"
        :style="{ width: '40rem', maxHeight: 'calc(100vh - 14rem)' }"
      >
        <div v-if="modalLoading" class="grid place-items-center h-100">
          <div class="blum-loader"></div>
        </div>
        <div v-else class="flex flex-col gap-4 px-8">
          <div class="flex items-center gap-2">
            <BlIcon icon="user" color="slate-6" class="w-6 h-6" />
            <div
              v-if="kindInput === 'for_therapist'"
              class="flex items-center gap-2"
            >
              <BlSelectBox
                class="w-96"
                search
                v-model="therapistInput"
                placeholder="Select therapist"
                :options="defaultTherapistOptions"
                :fetchProperty="{
                  url: `/api/v1/users?sort=name_asc&status=active&query=`,
                  dataKey: 'users',
                  countKey: 'total_users',
                  optionKeys: { value: 'id', label: 'name' }
                }"
                :disabled="selectedTherapist !== null"
                @change="therapistInput = $event"
              />
              <BlInputButton
                type="plain"
                label="Set as open room"
                :disabled="selectedTherapist !== null"
                @clicked="kindInput = 'open_room'"
              />
            </div>
            <div
              v-if="kindInput === 'open_room'"
              class="flex items-center gap-2"
            >
              <BlSelectBox
                key="open-room-placeholder"
                class="w-112"
                v-model="openRoomInput"
                :options="[
                  { value: 0, label: 'Open room - available to everyone' }
                ]"
                disabled
              />
              <BlInputButton
                type="plain"
                label="Remove"
                @clicked="kindInput = 'for_therapist'"
              />
            </div>
          </div>
          <div class="flex items-center gap-2">
            <BlIcon icon="calendar-blank" color="slate-6" class="w-6 h-6" />
            <BlInputDate
              class="flex-grow"
              :input="startDateInput"
              @changed="inputHandler"
            />
          </div>
          <div class="flex items-center gap-2">
            <BlIcon icon="clock-outline" color="slate-6" class="w-6 h-6" />
            <BlSelectBox
              class="w-64"
              v-model="startTimeInput"
              search
              placeholder="From"
              :options="startTimeOptions"
              @change="startTimeInput = $event"
            />
            <BlIcon icon="arrow-right" color="slate-6" class="w-6 h-6" />
            <BlSelectBox
              :key="`end-time-${startTimeInput}`"
              class="w-62"
              v-model="endTimeInput"
              search
              placeholder="To"
              :options="endTimeOptions"
              @change="endTimeInput = $event"
            />
          </div>
          <div v-if="enableBranch" class="flex items-center gap-2">
            <BlIcon icon="location" color="slate-6" class="w-6 h-6" />
            <BlSelectBox
              class="w-136"
              type="branch"
              search
              placeholder="Select branch"
              v-model="branchInput"
              :options="defaultBranchOptions"
              :fetchProperty="{
                url: `/api/v1/branches?sort=name_asc&query=`,
                dataKey: 'branches',
                countKey: 'total_count',
                optionKeys: {
                  value: 'id',
                  label: 'name',
                  color: 'color',
                  backgroundColor: 'background_color'
                }
              }"
              :disabled="!endTimeInput || selectedRoom !== null"
              @change="branchInput = $event"
            />
          </div>
          <div class="flex items-center gap-2">
            <BlIcon icon="door" color="slate-6" class="w-6 h-6" />
            <BlSelectBox
              :key="`room-${branchInput}`"
              class="w-136"
              search
              v-model="roomInput"
              :placeholder="roomSelectLoading ? 'Loading...' : 'Select room'"
              :options="roomOptions"
              :disabled="!endTimeInput || selectedRoom !== null"
              @change="roomInput = $event"
            />
          </div>
          <div class="flex items-center gap-2">
            <BlIcon icon="repeat" color="slate-6" class="w-6 h-6" />
            <BlSelectBox
              class="max-w-136"
              v-model="repeatInput"
              :options="repeatOptions"
              @change="onChangeRepeat($event)"
              :disabled="!startDateInput.model"
            />
          </div>
          <div class="flex items-center gap-2">
            <BlIcon icon="calendar-x" color="slate-6" class="w-6 h-6" />
            <BlInputDate
              class="flex-grow"
              :input="{
                model: displayEndAfter,
                disabled: true
              }"
            />
            <BlIcon icon="horizontal-arrow" color="slate-6" class="w-6 h-6" />
            <BlInputText
              class="flex-grow"
              :input="{
                inputType: 'number',
                model: displayOccurrences,
                suffix: 'time(s)',
                disabled: true
              }"
            />
          </div>
          <div
            v-if="conflictedByRoom.length || conflictedByUser.length"
            class="flex items-start gap-2 px-3 py-2 rounded bg-tulip-100"
          >
            <BlIcon icon="warning" color="tulip-7" class="w-6 h-6" />
            <div class="flex flex-col w-128">
              <div v-if="conflictedByRoom.length" class="flex flex-col">
                <div>
                  <BlTextBody2 :text="suffixRoomError" class="text-tulip-700" />
                </div>
                <div
                  v-for="(date, idx) in conflictedByRoom"
                  :key="idx"
                  class="flex items-center gap-2 px-2"
                >
                  <div class="w-1 h-1 rounded bg-tulip-700"></div>
                  <BlTextBody2 :text="date" class="text-tulip-700" />
                </div>
              </div>
              <div v-if="conflictedByUser.length" class="flex flex-col">
                <div>
                  <BlTextBody2
                    v-if="conflictedByRoom.length"
                    text="Additionally,"
                    class="text-tulip-700"
                  />
                  <BlTextBody2 :text="suffixUserError" class="text-tulip-700" />
                </div>
                <div
                  v-for="(date, idx) in conflictedByUser"
                  :key="idx"
                  class="flex items-center gap-2 px-2"
                >
                  <div class="w-1 h-1 rounded bg-tulip-700"></div>
                  <BlTextBody2 :text="date" class="text-tulip-700" />
                </div>
              </div>
              <BlTextBody2
                text="Please adjust the details to proceed with the availability."
                class="text-tulip-700"
              />
            </div>
          </div>
        </div>
      </div>
    </BlBackdrop>

    <RepeatDetailsModal
      v-if="repeatDetailsOpened"
      title="create"
      :current="{
        startDateInput: startDateInput.model,
        intervalInput,
        repetitionTypeInput,
        repeatEndInput,
        daysOfWeekInput,
        dayOfMonthInput,
        weekOfMonthInput,
        endAfterInput,
        occurrencesInput
      }"
      @close-modal="repeatDetailsOpened = false"
      @on-submit="onChangeRepeatDetails"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { displayDate } from "@/plugins/helper.js";
import moment from "moment";

import RepeatDetailsModal from "./RepeatDetailsModal.vue";

import BlBackdrop from "../../BlBackdrop.vue";
import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextBody3 from "../../BlTextBody3.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlIcon from "../../BlIcon.vue";
import BlInputText from "../../BlInputText.vue";
import BlInputDate from "../../BlInputDate.vue";
import BlInputRadio from "../../BlInputRadio.vue";
import BlSelectBox from "../../BlSelectBox.vue";
import BlInputButton from "../../BlInputButton.vue";

export default {
  name: "NewAvailabilitiesModal",
  components: {
    RepeatDetailsModal,
    BlBackdrop,
    BlTextSubtitle3,
    BlTextBody3,
    BlTextBody2,
    BlIcon,
    BlInputText,
    BlInputDate,
    BlInputRadio,
    BlSelectBox,
    BlInputButton
  },
  props: {
    selectedTherapist: { type: Object, default: null },
    selectedRoom: { type: Object, default: null },
    selectedStartDate: { type: String, default: "" },
    selectedStartTime: { type: String, default: "" }
  },
  data() {
    return {
      modalLoading: true,
      submitLoading: false,
      //
      kindInput: "for_therapist", // for_therapist | open_room
      therapistInput: 0,
      openRoomInput: 0,
      startDateInput: { model: "" },
      startTimeInput: "",
      endTimeInput: "",
      //
      branchInput: 0,
      roomInput: 0,
      rooms: [],
      roomSelectLoading: false,
      repeatInput: "not_repeat",
      //
      repeatDetailsOpened: false,
      customized: false,
      //
      intervalInput: "1",
      repetitionTypeInput: "week", // day | week | month | year
      repeatEndInput: "never", // never | on_date | after_occurrences
      //
      daysOfWeekInput: [], // (monday | tuesday | wednesday | thursday | friday | saturday | sunday)[]
      dayOfMonthInput: null, // 1 - 31 (day)
      weekOfMonthInput: null, // first, second, third, fourth, last (week)
      //
      endAfterInput: "",
      occurrencesInput: "",
      conflictedByRoom: [],
      conflictedByUser: []
    };
  },
  computed: {
    ...mapGetters(["currentUser", "currentCenter", "user", "room"]),
    enableBranch() {
      return this.currentCenter.enable_branch;
    },
    slotType() {
      // room, therapist
      return this.$route.params.tab || "room";
    },
    defaultTherapistOptions() {
      const therapist = this.selectedTherapist;
      if (!therapist) return [];
      return [{ value: therapist.id, label: therapist.name }];
    },
    defaultBranchOptions() {
      const arr = [
        { value: 0, label: "All branches", class: "-mx-2" },
        {
          value: "no",
          label: "No branch",
          color: "#667085",
          backgroundColor: "#F9FAFB"
        }
      ];

      const branch = this.selectedRoom && this.selectedRoom.branch;
      if (branch) {
        arr.unshift({
          value: branch.id,
          label: branch.name,
          color: branch.color,
          backgroundColor: branch.background_color
        });
      }
      return arr;
    },
    roomOptions() {
      let rooms = [...this.rooms];
      if (this.enableBranch && this.branchInput) {
        rooms = rooms.filter(i => {
          if (this.branchInput === "no") return !i.branch_id;
          return i.branch_id === this.branchInput;
        });
      }

      const arr = rooms.map(i => {
        let branch = "";
        if (this.enableBranch && !this.branchInput && i.branch_id) {
          branch = `
            <div
              class="flex items-center justify-center px-2 -mx-4 truncate rounded max-w-32 h-7"
              style="background-color: ${i.branch.background_color || "unset"}"
            >
              <span
                class="text-sm truncate"
                style="color: ${i.branch.color || "unset"}"
              >
                ${i.branch.name}
              </span>
            </div>
          `;
        }
        return {
          value: i.id,
          label: i.name,
          disabled: i.is_booked,
          suffix: i.is_booked
            ? `<div class="flex items-center gap-8">${branch} Booked</div>`
            : branch
        };
      });

      if (this.selectedRoom) {
        arr.unshift({
          value: this.selectedRoom.id,
          label: this.selectedRoom.name
        });
      }

      return arr;
    },
    startTimeOptions() {
      const arr = Array.from({ length: 48 }, (v, i) => {
        const inMinutes = i * 30;
        const hours = String(Math.floor(inMinutes / 60)).padStart(2, "0");
        const minutes = inMinutes % 60 === 0 ? "00" : "30";
        const time = `${hours}:${minutes}`;
        return { value: time, label: time };
      });
      return arr;
    },
    endTimeOptions() {
      const arr = Array.from({ length: 48 }, (v, i) => {
        const inMinutes = (i + 1) * 30;
        const hours = String(Math.floor(inMinutes / 60)).padStart(2, "0");
        const minutes = inMinutes % 60 === 0 ? "00" : "30";
        const time = `${hours}:${minutes}`;
        return { value: time, label: time };
      });
      if (!this.startTimeInput) return arr;
      const start = this.startTimeInput.split(":").map(i => Number(i));
      return arr.filter(i => {
        const val = i.value.split(":").map(a => Number(a));
        if (val[0] > start[0]) return true;
        else if (val[0] === start[0] && val[1] > start[1]) return true;
        else return false;
      });
    },
    repeatOptions() {
      const arr = [{ value: "not_repeat", label: `Doesn't repeat` }];

      if (this.customized) {
        const collectiveTerms = {
          day: "Daily",
          week: "Weekly",
          month: "Monthly",
          year: "Annually"
        };
        const repeatNumber =
          this.intervalInput > 1
            ? `Every ${this.intervalInput} ${this.repetitionTypeInput}s`
            : collectiveTerms[this.repetitionTypeInput];

        let onDaysOfWeek = "on ";
        onDaysOfWeek += this.daysOfWeekInput
          .map(i => this.uppercaseFirstLetter(i))
          .join(", ");

        const onDateOfMonth = `on day ${this.dayOfMonthInput}`;

        const ordinals = {
          first: "1st",
          second: "2nd",
          third: "3rd",
          fourth: "4th",
          last: "5th"
        };
        const ordinal = ordinals[this.weekOfMonthInput];
        const dayOfWeek = moment(this.startDateInput.model).format("dddd");
        const onWeekOfMonth = `on the ${ordinal} ${dayOfWeek}`;

        const onMonthDateOfYear = `on ${moment(
          this.startDateInput.model
        ).format("MMMM DD")}`;

        const repeatOn = {
          day: "",
          week: onDaysOfWeek,
          month: this.dayOfMonthInput ? onDateOfMonth : onWeekOfMonth,
          year: onMonthDateOfYear
        };

        const label = `${repeatNumber} ${repeatOn[this.repetitionTypeInput]}`;
        arr.push({ value: "customized", label });
      }

      arr.push({ value: "custom", label: `Custom...` });
      return arr;
    },
    displayEndAfter() {
      if (!this.repeatInput.includes("custom")) {
        return this.startDateInput.model;
      }
      if (this.repeatEndInput === "never") return this.startDateInput.model;
      if (this.repeatEndInput === "on_date") return this.endAfterInput;
      return this.startDateInput.model;
    },
    displayOccurrences() {
      if (!this.repeatInput.includes("custom")) return 1;
      if (this.repeatEndInput === "never") return 1;
      if (this.repeatEndInput === "after_occurrences") {
        return this.occurrencesInput;
      }
      return 1;
    },
    isDisabledSubmit() {
      if (this.slotType === "room") {
        if (!this.kindInput) return true;
      }
      if (this.slotType === "therapist" || this.kindInput === "for_therapist") {
        if (!this.therapistInput) return true;
      }
      if (!this.startDateInput.model) return true;
      if (!this.startTimeInput) return true;
      if (!this.endTimeInput) return true;
      if (!this.roomInput) return true;
      return false;
    },
    //
    suffixRoomError() {
      const name = this.room && this.room.name;
      return `${name} isn't available for the selected timeslot (${this.startTimeInput} - ${this.endTimeInput}) on the following day(s):`;
    },
    suffixUserError() {
      const name = this.user && this.user.name;
      return `${name} cannot use this room on the following day(s) due to existing availabilities and/or appointments:`;
    }
  },
  watch: {
    therapistInput: {
      immediate: true,
      handler(val) {
        this.getUser({ id: val });
      }
    },
    roomInput: {
      immediate: true,
      handler(val) {
        this.getRoom({ id: val });
      }
    },
    //
    startDateInput() {
      this.endTimeInput = "";
    },
    startTimeInput() {
      this.endTimeInput = "";
    },
    endTimeInput(val) {
      if (val) this.fetchRooms();
    },
    branchInput(val) {
      if (this.enableBranch) {
        if (val) this.roomInput = 0;
      }
    }
  },
  async mounted() {
    this.modalLoading = true;

    if (this.selectedTherapist) this.therapistInput = this.selectedTherapist.id;
    if (this.selectedRoom) {
      if (this.selectedRoom.branch) {
        this.branchInput = this.selectedRoom.branch.id;
      }
      setTimeout(() => {
        this.roomInput = this.selectedRoom.id;
      }, 10);
    }

    if (this.selectedStartDate) {
      this.startDateInput.model = this.selectedStartDate;
    }
    if (this.selectedStartTime) {
      this.startTimeInput = this.selectedStartTime;
    }

    this.modalLoading = false;
  },
  async destroyed() {},
  methods: {
    ...mapActions(["actionGet", "getUser", "getRoom", "createAvailabilities"]),
    displayDate,
    inputHandler(payload) {
      payload.input.model = payload.value;
    },
    uppercaseFirstLetter(string) {
      return string.charAt(0).toUpperCase() + string.slice(1);
    },
    async fetchRooms() {
      const params = {
        url: `/api/v1/rooms?date=${this.startDateInput.model}&start_time=${this.startTimeInput}&end_time=${this.endTimeInput}`
      };

      this.roomSelectLoading = true;
      const { success, data } = await this.actionGet(params);
      this.roomSelectLoading = false;

      if (!success) return;
      this.rooms = data.rooms;
    },
    getSlotCount(start, end) {
      const s = start.split(":").map(i => Number(i));
      const e = end.split(":").map(i => Number(i));
      let slots = 0;
      slots += (e[0] - s[0]) * 2;
      if (s[1] === 30) slots -= 1;
      if (e[1] === 30) slots += 1;
      return slots;
    },
    onChangeRepeat(val) {
      this.repeatInput = val;
      if (val === "custom") this.repeatDetailsOpened = true;
    },
    onChangeRepeatDetails(val) {
      this.customized = true;
      this.repeatInput = "customized";

      this.intervalInput = val.intervalInput;
      this.repetitionTypeInput = val.repetitionTypeInput; // day | week | month | year
      this.repeatEndInput = val.repeatEndInput; // never | on_date | after_occurrences

      this.daysOfWeekInput = val.daysOfWeekInput; // (monday | tuesday | wednesday | thursday | friday | saturday | sunday)[]
      this.dayOfMonthInput = val.dayOfMonthInput; // 1 - 31 (day)
      this.weekOfMonthInput = val.weekOfMonthInput; // first, second, third, fourth, last (week)

      this.endAfterInput = val.endAfterInput;
      this.occurrencesInput = val.occurrencesInput;
    },
    async onSubmit() {
      const params = {
        room_name: this.room.name,
        availability: {
          user_id: this.kindInput === "open_room" ? null : this.therapistInput,
          room_id: this.roomInput,
          start_date: this.startDateInput.model,
          start_time: this.startTimeInput,
          end_time: this.endTimeInput,
          //
          interval: null,
          repetition_type: "not_repeat",
          days_of_week: [],
          day_of_month: null,
          week_of_month: null,
          end_after: null,
          occurrences: null
        },
        message: null
      };

      if (this.repeatInput.includes("custom")) {
        params.availability.interval = this.intervalInput;
        params.availability.repetition_type = this.repetitionTypeInput;
        if (this.repetitionTypeInput === "week") {
          params.availability.days_of_week = this.daysOfWeekInput;
        }
        if (this.repetitionTypeInput === "month") {
          params.availability.day_of_month = this.dayOfMonthInput;
          params.availability.week_of_month = this.weekOfMonthInput;
        }
        if (this.repeatEndInput === "on_date") {
          params.availability.end_after = this.endAfterInput;
        }
        if (this.repeatEndInput === "after_occurrences") {
          params.availability.occurrences = this.occurrencesInput;
        }
      }

      this.conflictedByRoom = [];
      this.conflictedByUser = [];
      this.submitLoading = true;
      const {
        success,
        conflicted_by_room: byRoom,
        conflicted_by_user: byUser
      } = await this.createAvailabilities(params);
      if (!success) {
        this.submitLoading = false;
        const format = "DD MMM YYYY";
        this.conflictedByRoom = byRoom.map(date =>
          this.displayDate({ date, format })
        );
        this.conflictedByUser = byUser.map(date =>
          this.displayDate({ date, format })
        );
        return;
      }

      setTimeout(() => {
        this.submitLoading = false;
        this.$emit("after-commit");
        this.$emit("close-modal");
      }, 1000);
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/availabilities/timeslot/NewAvailabilitiesModal.vue